import { useEffect, useRef, useState, type FormEvent } from "react";
import { Marke } from "../ui/Marke";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router";
import { Button, Illustration, Notice, TextField } from "../ui";
import { api, ApiFehler, rufe } from "../api/client";
import { useNavigate } from "react-router";
import { passkeyAnmelden, passkeysMoeglich } from "../api/passkey";
import { RechtlichesFuss } from "./Rechtliches";

type Ziel = "laden" | "mieter" | "admin" | "kasse";

declare global {
  interface Window {
    turnstile?: { render: (el: HTMLElement, opts: { sitekey: string; callback: (token: string) => void; "expired-callback"?: () => void; theme?: "auto" | "light" | "dark"; language?: string; size?: "normal" | "flexible" | "compact" }) => string; reset: (id?: string) => void };
  }
}

/** Turnstile-Widget (Etappe 7): nur wenn der Laden einen Site-Key hat; liefert das Token für /api/auth/link. */
function Turnstile({ siteKey, onToken }: { siteKey: string; onToken: (t: string | null) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let id: string | undefined;
    const rendern = () => {
      if (!ref.current || !window.turnstile || id) return;
      id = window.turnstile.render(ref.current, { sitekey: siteKey, callback: onToken, "expired-callback": () => onToken(null), theme: "auto", language: "de", size: "flexible" });
    };
    if (window.turnstile) rendern();
    else {
      const s = document.createElement("script");
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      s.async = true;
      s.onload = rendern;
      document.head.appendChild(s);
    }
  }, [siteKey]);
  return <div ref={ref} />;
}

/** Anmeldung per E-Mail-Link (Frage 1): ein Weg für alle Rollen. Danach „Link gesendet“. */
export function Anmelden() {
  const [params] = useSearchParams();
  const ziel = (["laden", "mieter", "admin", "kasse"].includes(params.get("ziel") ?? "") ? params.get("ziel") : "laden") as Ziel;
  const laden = params.get("laden") ?? undefined;
  const weiterParam = params.get("weiter") ?? "";
  const weiter = /^\/[a-z0-9\-/]*$/i.test(weiterParam) && weiterParam.startsWith(`/${ziel}`) ? weiterParam : undefined;
  const branding = useQuery({ queryKey: ["public", "laden", laden ?? ""], queryFn: () => rufe<{ name: string; akzent: string | null; logo?: boolean }>(api.api.public.laden[":slug"].$get({ param: { slug: laden! } })), enabled: !!laden, retry: false });
  useEffect(() => {
    const akzent = branding.data?.akzent ?? null;
    if (akzent) document.documentElement.style.setProperty("--accent", akzent);
    return () => {
      document.documentElement.style.removeProperty("--accent");
    };
  }, [branding.data?.akzent]);
  const ladenName = branding.data?.name ?? null;
  const config = useQuery({ queryKey: ["public", "config"], queryFn: () => rufe<{ turnstileSiteKey: string | null }>(api.api.public.config.$get()), staleTime: Infinity });
  const siteKey = config.data?.turnstileSiteKey ?? null;
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [gesendet, setGesendet] = useState<string | null>(null);
  const [erneutIn, setErneutIn] = useState(0);
  const navigate = useNavigate();
  const [passkeyLaeuft, setPasskeyLaeuft] = useState(false);
  async function mitPasskey() {
    if (ziel === "kasse") return;
    setFehler(null);
    setPasskeyLaeuft(true);
    try {
      const r = await passkeyAnmelden(ziel, laden);
      navigate(weiter ?? r.ziel, { replace: true });
      window.location.reload();
    } catch (err) {
      if (err instanceof DOMException && err.name === "NotAllowedError") setFehler("Passkey abgebrochen. Du kannst dich auch per E-Mail-Link anmelden.");
      else setFehler(err instanceof ApiFehler ? err.message : "Anmeldung mit Passkey hat nicht geklappt. Bitte per E-Mail-Link anmelden.");
    } finally {
      setPasskeyLaeuft(false);
    }
  }

  async function senden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setFehler("Bitte eine gültige E-Mail-Adresse eingeben.");
      return;
    }
    setLaeuft(true);
    try {
      await rufe(api.api.auth.link.$post({ json: { email: email.trim(), ziel, laden, weiter, turnstile: turnstileToken ?? undefined } }));
      setGesendet(email.trim());
      setErneutIn(60);
      const t = setInterval(() => setErneutIn((s) => (s <= 1 ? (clearInterval(t), 0) : s - 1)), 1000);
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : "Das hat nicht geklappt. Bitte noch einmal versuchen.");
    } finally {
      setLaeuft(false);
    }
  }

  const duzen = ziel === "mieter";

  if (gesendet) {
    return (
      <div className="app-public">
        <div className="app-public__card sc-center" style={{ padding: 0 }}>
          <Illustration name="all-done" />
          <h1 className="display">{duzen ? "Schau in dein Postfach" : "Bitte ins Postfach schauen"}</h1>
          <p>
            {duzen ? "Wir haben dir einen Anmeldelink geschickt an" : "Ein Anmeldelink wurde geschickt an"} <strong>{gesendet}</strong>.
          </p>
          <p className="sc-muted">{duzen ? "Tippe in der E-Mail auf „Jetzt anmelden“. Nichts angekommen? Schau auch im Spam-Ordner nach." : "In der E-Mail auf „Jetzt anmelden“ tippen. Nichts angekommen? Auch den Spam-Ordner prüfen."}</p>
          <Button variant="secondary" icon="mail" block disabled={erneutIn > 0 || laeuft} loading={laeuft} onClick={(ev) => void senden(ev as unknown as FormEvent)}>
            {erneutIn > 0 ? `Erneut senden in 0:${erneutIn.toString().padStart(2, "0")}` : "Link erneut senden"}
          </Button>
          <Button variant="quiet" onClick={() => setGesendet(null)}>
            Andere E-Mail-Adresse verwenden
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-public">
      <form className="app-public__card" onSubmit={senden} noValidate>
        <div className="app-public__brand">
          {branding.data?.logo ? <img src={`/api/public/laden/${laden}/logo`} alt="" className="app-logo" width={40} height={40} /> : <Marke size={40} />}
          <span className="sc-strong">{ladenName ?? "MeinRegalladen"}</span>
        </div>
        <h1 className="title">{duzen ? (ladenName ? `Willkommen bei ${ladenName}` : "Willkommen") : "Anmelden"}</h1>
        <p className="sc-muted">
          {duzen
            ? "Melde dich mit deiner E-Mail-Adresse an. Hier siehst du dein Guthaben, deine Artikel und deine Abrechnungen."
            : "Mit der E-Mail-Adresse anmelden. Ein Passwort gibt es nicht – der Link kommt per E-Mail."}
        </p>
        <TextField
          label={duzen ? "Deine E-Mail-Adresse" : "E-Mail-Adresse"}
          type="email"
          icon="mail"
          autoComplete="email"
          inputMode="email"
          placeholder="name@beispiel.de"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={fehler ?? undefined}
          autoFocus
        />
        {siteKey ? <Turnstile siteKey={siteKey} onToken={setTurnstileToken} /> : null}
        <Button type="submit" variant="primary" size="lg" icon="mail" block loading={laeuft} disabled={!!siteKey && !turnstileToken}>
          Anmeldelink senden
        </Button>
        {passkeysMoeglich() && ziel !== "kasse" ? (
          <Button variant="secondary" size="lg" icon="shield-check" block loading={passkeyLaeuft} onClick={() => void mitPasskey()}>
            Mit Passkey anmelden
          </Button>
        ) : null}
        <Notice tone="info" icon="lock" title="Kein Passwort nötig">
          {duzen ? "Wir schicken dir einen Link per E-Mail. Ein Tipp darauf, und du bist angemeldet." : "Der Link gilt 15 Minuten und nur einmal."}
        </Notice>
        {ziel !== "kasse" && ziel !== "mieter" ? <p className="sc-muted sc-small sc-center">Neu hier? <Link to="/registrieren">Laden kostenlos testen</Link></p> : null}
        <RechtlichesFuss />
      </form>
    </div>
  );
}
